import React from 'react';
import { FeatureFlags, Filter, PeriodBehavior, RelativeUnit } from '@fabric-ui/components';
import { TimeBucket } from '@fabric-ui/components/Table';
import { flex } from '@fabric-ui/theme';
import { Box, Collapse, Typography, useMediaQuery, useTheme } from '@mui/material';
import { addCustomFiltersAsExpression, cleanEmptyFilters, filterToExpression } from '@utils/filterUtils';
import { useAvContext } from '../../../context/AvContextProvider';
import DraggableWidget from '../../CustomDashboards/components/DraggableWidget';
import renderWidget from '../../CustomDashboards/components/Widgets/RenderWidget';
import {
  DwQueryRequest,
  PieBasicLegendPosition,
  PieCategoryWidgetTypes,
  PieLegendStyles,
  PieSubType,
  SortDir,
  WidgetCategory,
} from '../../CustomDashboards/types';
import { getEntityOverTimeWidget } from '../../Policies/AssetInventoryDashboard/Widgets';
import { filtersFieldToIgnore, IDENTITY_FINDING_RISK_SCORE } from '../ticket.types';
import RiskScoreSeverityWidget from './RiskScoreSeverityWidget';
import { IEntityViewChartsProps } from './types';
import { entityViewWidgetStyle, EntityViewWidgetWrapper } from './utils';

const getIdentityFindingsBySeverityWidget = ({
  filters,
  isSmallScreen,
}: {
  filters: Filter;
  isSmallScreen: boolean;
}): PieCategoryWidgetTypes => ({
  definition: {
    title: { title: 'Identity Findings By Severity' },
    coords: { x: 0, y: 0, h: 3, w: 0 },
    custom: {
      showTotals: true,
      legend: {
        position: PieBasicLegendPosition.Top,
        style: PieLegendStyles.Table,
        showValues: !isSmallScreen,
        showPercentage: false,
      },
      fieldsPalette: {},
    },
  },
  category: WidgetCategory.Pie,
  type: PieSubType.Donut,
  requests: [getIdentityFindingsBySeverityRequest(filters)],
});

const getIdentityFindingSeverityOverTimeWidget = ({ filter }) =>
  getEntityOverTimeWidget({
    title: 'Identity Findings Severity Over Time',
    req: getIdentityFindingSeverityOverTimeRequest({ filter }),
  });

const getIdentityFindingSeverityOverTimeRequest = ({
  filter,
  metricName = 'total_identity_findings',
}: {
  filter?: Filter;
  metricName?: string;
}): DwQueryRequest => ({
  distinct: false,
  filter,
  select: {
    metrics: [
      {
        name: metricName,
        alias: metricName,
      },
    ],
    dims: [
      {
        name: TimeBucket.BUCKET_MONTH,
      },
      {
        name: 'identity_finding.severity',
      },
    ],
  },
  groupBy: [TimeBucket.BUCKET_MONTH, 'identity_finding.severity'],
  sorting: [
    {
      name: TimeBucket.BUCKET_MONTH,
      dir: SortDir.ASC,
    },
  ],
  top: {
    size: 1000,
    groupOthers: false,
    offset: 0,
  },
  projectionId: {
    name: 'uber_identity_findings',
    builtIn: true,
  },
  timeRange: {
    relative: {
      unit: RelativeUnit.MONTHS,
      value: 3,
      periodBehaviour: PeriodBehavior.last,
    },
  },
});

const getIdentityFindingsBySeverityRequest = (filters: Filter): DwQueryRequest => ({
  filter: filters && Object.keys(filters).length ? filters : undefined,
  select: {
    dims: [{ name: 'identity_finding.severity' }],
    metrics: [{ name: 'total_identity_findings', alias: 'total_identity_findings' }],
  },
  groupBy: ['identity_finding.severity'],
  sorting: [{ name: 'identity_finding.severity', dir: SortDir.DESC }],
  top: { size: 4, groupOthers: false },
  projectionId: { name: 'uber_identity_findings', builtIn: true },
  distinct: false,
  metricFilterRootProj: true,
});

const IdentityFindingsViewCharts: React.FC<IEntityViewChartsProps> = ({ filters, isExpanded = true, setIsExpanded, searchFields }) => {
  const { measurements, accountEntities, featureFlags } = useAvContext();
  const isNewRiskScoreEnabled = !!featureFlags[FeatureFlags.NewRiskScore];
  const { fieldTypeMap } = accountEntities;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(1501));
  const filteredFilters = Object.keys(filters).reduce(
    (acc, field) =>
      !filtersFieldToIgnore.includes(field) && !!filters[field].length
        ? {
            ...acc,
            [field]: filters[field],
          }
        : acc,
    {}
  );

  const regularFilters = filterToExpression({ ...fieldTypeMap, ...measurements.asObject }, filteredFilters);

  const customFilters = addCustomFiltersAsExpression({
    expressionFiltersObj: cleanEmptyFilters({ and: { operands: [regularFilters] } }),
    operand: 'or',
    operator: 'contains',
    fields: searchFields,
    value: filters?.search?.[0],
    fieldTypeMap,
  });

  const identityFindingsBySeverityWidget = getIdentityFindingsBySeverityWidget({ filters: customFilters, isSmallScreen });
  const identityFindingsSeverityOverTimeWidget = getIdentityFindingSeverityOverTimeWidget({ filter: customFilters });

  const widgetsToShow = [identityFindingsSeverityOverTimeWidget, identityFindingsBySeverityWidget];

  return (
    <EntityViewWidgetWrapper isExpanded={isExpanded} setIsExpanded={setIsExpanded} summaryItemsProps={{ items: [] }}>
      <Collapse in={isExpanded} timeout="auto">
        <Box sx={{ ...flex.row, gap: 1.5, height: '208px' }}>
          {widgetsToShow.map(widget => (
            <Box
              key={widget.definition.title.title}
              sx={{
                ...entityViewWidgetStyle,
                position: 'relative',
                backgroundColor: theme => theme.palette.white.main,
              }}>
              {isNewRiskScoreEnabled ? (
                <RiskScoreSeverityWidget
                  widget={widget}
                  riskScoreField={IDENTITY_FINDING_RISK_SCORE}
                  severityField="identity_finding.severity"
                />
              ) : (
                <DraggableWidget
                  widget={widget}
                  customToolbar={
                    <Typography sx={{ whiteSpace: 'nowrap', marginBottom: '8px' }} variant="h6">
                      {widget.definition.title.title}
                    </Typography>
                  }>
                  {renderWidget({ widget })}
                </DraggableWidget>
              )}
            </Box>
          ))}
        </Box>
      </Collapse>
    </EntityViewWidgetWrapper>
  );
};

export default IdentityFindingsViewCharts;
